import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import { apiUserService } from '@api';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import DragAndDrop from '@components/drag_and_drop';
import { fileToBase64 } from '@utils/index';
import Button from '@components/common/button';
import Webcam from 'react-webcam';
import { useUserContext } from '@hooks/context/useUserContext';
import { useApiMutation } from '@hooks/api/useApiMutation';
import selfieOverlay from '@svg/selfie_overlay.svg';
import documentOverlay from '@svg/license_overlay.svg';
import { EUserStatus } from '@xeppt/xeppt-sdk/types';

interface IProps {
    handleNextStep: () => void;
    onClose: () => void;
}

const KycDocuments: FC<IProps> = ({ handleNextStep, onClose }) => {
    const { t } = useTranslation('modals', {
        keyPrefix: 'kyc.first'
    });
    const { requestErrorLocale, requestSuccessLocale, submitLocale, labelLocale } = useLocales();
    const [disabled, setDisabled] = useState(false);
    const [selfie, setSelfie] = useState('');
    const [document, setDocument] = useState('');
    const webcamRef = useRef<any>(null);
    const [isWebcam, setIsWebcam] = useState(false);
    const [activeDocument, setActiveDocument] = useState<'selfie' | 'doc'>('selfie');
    const { handleChangeUserStatus } = useUserContext();

    useEffect(() => {
        if (!selfie || !document) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
    }, [document, selfie]);

    const { handleRequest: onSubmit, isLoading } = useApiMutation({
        method: () =>
            apiUserService.verify({
                face: selfie,
                document
            }),
        onSuccess: () => {
            requestSuccessLocale('kyc_start');
            handleNextStep();
            handleChangeUserStatus(EUserStatus.PENDING);
        },
        onError: requestErrorLocale
    });

    const handleMakePhoto = useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot();
        if (activeDocument === 'doc') {
            setDocument(imageSrc);
        } else {
            setSelfie(imageSrc);
        }
        setIsWebcam(false);
    }, [webcamRef, activeDocument]);

    return (
        <div className={styles.uploader_wrapper}>
            <Typography variant="h4" alignment="center" className={styles.header_title}>
                {t('title')}
            </Typography>
            {!isWebcam ? (
                <>
                    <div className={`${styles.content} ${styles.uploaders}`}>
                        <div className={styles.uploader}>
                            <Typography
                                variant="body3"
                                weight="semibold"
                                alignment="center"
                                className={styles.uploader_title}>
                                1. Provide passport or driver's license
                            </Typography>
                            <DragAndDrop
                                icon="upload_cloud"
                                isPreview
                                isDraggable
                                // disabled
                                accept={['image/jpeg', 'image/jpg']}
                                label={labelLocale('drag_drop_file')}
                                isShowDescription={false}
                                preview={document}
                                onChange={async (selfie) => {
                                    setDocument(selfie ? await fileToBase64(selfie) : '');
                                }}
                                isAcceptImages
                                onTakePhoto={() => {
                                    setIsWebcam(true);
                                    setActiveDocument('doc');
                                }}
                            />
                        </div>
                        <div className={styles.uploader}>
                            <Typography
                                variant="body3"
                                weight="semibold"
                                alignment="center"
                                className={styles.uploader_title}>
                                2. Take a selfie
                            </Typography>
                            <DragAndDrop
                                isPreview
                                isDraggable
                                label={labelLocale('take_selfie_camera')}
                                preview={selfie}
                                description="Make sure your face is clearly visible"
                                isUpload={false}
                                accept={['image/jpeg', 'image/jpg']}
                                onChange={async (doc) => {
                                    setSelfie(doc ? await fileToBase64(doc) : '');
                                }}
                                onTakePhoto={() => {
                                    setIsWebcam(true);
                                    setActiveDocument('selfie');
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.actions}>
                        <Button size="medium" variant="outlined-primary" onClick={onClose}>
                            {submitLocale('not_now')}
                        </Button>
                        <Button
                            disabled={disabled}
                            size="medium"
                            variant="primary"
                            onClick={onSubmit}
                            isLoading={isLoading}>
                            {submitLocale('verify')}
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.webcam_wrapper}>
                        <Webcam
                            screenshotFormat="image/jpeg"
                            width="100%"
                            height="100%"
                            ref={webcamRef}
                        />
                        <img
                            className={styles.webcam_overlay}
                            src={activeDocument === 'selfie' ? selfieOverlay : documentOverlay}
                            alt=""
                        />
                    </div>
                    {activeDocument === 'selfie' && (
                        <Typography
                            variant="body4"
                            alignment="center"
                            className={styles.webcam_description}>
                            Adjust your position to place your face into oval frame, then press
                            'Take a {activeDocument === 'selfie' ? 'selfie' : 'photo'}'
                        </Typography>
                    )}
                    {activeDocument === 'doc' && (
                        <Typography
                            variant="body4"
                            alignment="center"
                            className={styles.webcam_description}>
                            Position the front of your passport or driver's license within the
                            frame, ensuring it's fully visible and all four corners of the document
                            are within the frame, then press 'Take a photo'
                        </Typography>
                    )}
                    <div className={styles.actions}>
                        <Button
                            size="medium"
                            variant="outlined-primary"
                            onClick={() => setIsWebcam(false)}>
                            {submitLocale('cancel')}
                        </Button>
                        <Button size="medium" variant="primary" onClick={handleMakePhoto}>
                            {submitLocale('make_photo')}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default KycDocuments;
